<template>
  <div>
    <div class="row mt-2 no-gutters evollu-form" v-if="showFilters">
      <div class="row">
        <RangeDatePicker
          id="selected-birthdate-picker"
          v-model="filters.date"
          class="col-12 col-md-4 datesFilter"
          :placeholder="$t('ranu.records.filters.birthdate')">

          <div slot="label">
            <label class="form-label" style="margin-top: -3px;">
              {{ $t('ranu.records.filters.birthdate') }}
            </label>
          </div>
        </RangeDatePicker>

        <!-- Statuses Filter -->
        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('ranu.records.status_now') }}:</label>
          <multiselect
            v-model="filters.statuses"
            :options="statusesOptions"
            track-by="id"
            label="name"
            select-label=""
            deselect-label="×"
            placeholder="---"
            :searchable="true"
            :allow-empty="true"
            :multiple="true"
            :limit="3"
            :limit-text="limitText">
            <span slot="noResult">{{ $t('patients.noResults') }}</span>
            <template slot="tag" slot-scope="{ option, remove }">
                      <span class="custom__tag">
                        <span>{{ $t(`ranu.statuses.${option.id}`, option.name) }}</span>
                        <i @click="filters.statuses = filters.statuses.filter(status => status.id !== option.id)"
                           aria-hidden="true" tabindex="1" class="custom__tag-icon"></i>
                      </span>
            </template>
            <template slot="option" slot-scope="{option}">
              {{ $t(`ranu.statuses.${option.id}`, option.name) }}
            </template>
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.statuses.length"
                   @mousedown.prevent.stop="filters.statuses = []"></div>
            </template>
          </multiselect>
        </div>

        <!-- Exams Filter -->
        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('ranu.records.exams_made') }}:</label>
          <div class="form-group" style="margin-bottom:1.25rem;">
            <div class="input-group clearable-filter">
              <multiselect
                v-model="filters.exams"
                :options="examTypesOptions"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :multiple="true"
                :limit="3"
                :limit-text="limitText"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
                <template slot="clear" slot-scope="props">
                  <div class="multiselect__clear" v-if="filters.exams.length"
                       @mousedown.prevent.stop="filters.exams = []"></div>
                </template>
              </multiselect>
            </div>
          </div>
        </div>

        <!-- Risk Factor Filter -->
        <div class="col-md-4">
          <label class="form-label">{{ $t('ranu.records.risk_factor') }}:</label>
          <div class="form-group" style="margin-bottom:1.25rem;">
            <div class="input-group clearable-filter">
              <multiselect
                v-model="filters.riskFactors"
                :options="riskFactorsOptions"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :multiple="true"
                :limit="2"
                :limit-text="limitText"
                :close-on-select="false">
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
                <template slot="clear" slot-scope="props">
                  <div class="multiselect__clear" v-if="filters.riskFactors.length"
                       @mousedown.prevent.stop="filters.riskFactors = []"></div>
                </template>
                <template slot="option" slot-scope="props">
                  <span>{{ props.option.name }}</span>
                  <span v-if="props.option.description">{{ ` - ${props.option.description}` }}</span>
                </template>
              </multiselect>
            </div>
          </div>
        </div>

        <!-- Entities Filter -->
        <div class="col-md-4" v-if="readEntities.length > 1">
          <label class="form-label">{{ $t('users.inputs.entity') }}:</label>
          <div class="form-group" style="margin-bottom:1.25rem;">
            <div class="input-group clearable-filter">
              <multiselect
                v-model="filters.entities"
                :options="readEntities"
                track-by="id"
                label="name"
                select-label=""
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :multiple="true"
                :limit="2"
                :limit-text="limitText"
                :close-on-select="false">
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
                <template slot="clear" slot-scope="props">
                  <div class="multiselect__clear" v-if="filters.entities.length"
                       @mousedown.prevent.stop="filters.entities = []"></div>
                </template>
              </multiselect>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-server-table
      id="ranuRecordsTable"
      ref="ranuRecordsTable"
      :name="'ranuRecordsTable'"
      :columns="columns"
      :options="tableOptions"
      @row-click="handleRecordClicked">

      <span slot="afterFilter">
        <va-button class="external-btn"
                   @click="handleShowExternalModal">{{ $t('ranu.records.external_patient') }}</va-button>
      </span>

      <span slot="process_id" slot-scope="props">
          <span v-if="props.row.process_id">{{ props.row.process_id }}</span>
          <span v-else>---</span>
        </span>

      <span slot="sns_number" slot-scope="props">
          <span v-if="props.row.sns_number">{{ props.row.sns_number }}</span>
          <span v-else>---</span>
        </span>

      <span slot="name" slot-scope="props">
          <span v-if="props.row.name">{{ props.row.name }}</span>
          <span v-else>---</span>
        </span>

      <span slot="birthdate" slot-scope="props">
          <span>{{ props.row.birthdate | localTime('YYYY-MM-DD HH:mm') }}</span>
        </span>

      <span slot="risk_factor" slot-scope="props">
          <div v-if="props.row.risk_factor">
            <va-popover
              placement="top"
              :message="props.row.risk_factor.description">
              <span>{{ props.row.risk_factor.name }}</span>
            </va-popover>
          </div>
          <div v-else>
            ---
          </div>
        </span>

      <span slot="created_at" slot-scope="{row}">
          {{ row.created_at }}
        </span>

      <span slot="mother" slot-scope="props">
          <span>{{ getParentByType('mother', props.row.contacts) || 'N/A' }}</span>
        </span>

      <span slot="external" slot-scope="props">
          <span v-if="props.row.external" class="fa fa-check"></span>
        </span>

      <span slot="status" slot-scope="props">
          <span v-if="props.row.status">{{ props.row.status.name }}</span>
        </span>

      <span slot="O.E.A.1" slot-scope="props">
          <span>{{ getExamResultByType('O.E.A.1', props.row.exams) }}</span>
        </span>

      <span slot="O.E.A.2" slot-scope="props">
          <span>{{ getExamResultByType('O.E.A.2', props.row.exams) }}</span>
        </span>

      <span slot="P.E.A.A." slot-scope="props">
          <span>{{ getExamResultByType('P.E.A.A.', props.row.exams) }}</span>
        </span>

      <span slot="Consulta" slot-scope="props">
          <span>{{ getExamResultByType('Consulta', props.row.exams) }}</span>
        </span>

      <span slot="P.E.A.T.C." slot-scope="props">
          <span>{{ getExamResultByType('P.E.A.T.C.', props.row.exams) }}</span>
        </span>

    </v-server-table>
    <ExternalPatientSearchModal
      v-if="showExternalModal"
      @canceled="showExternalModal = false"
      @consult="handleConsult"
      :entities="ranuEntities"
    />
  </div>

</template>
<script>

import TableQueryMixin from '@/mixins/TableQueryMixin'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import Multiselect from 'vue-multiselect'
import { mapState } from 'vuex'
import ExternalPatientSearchModal from '@/components/ranu/records/external-patient-modals/ExternalPatientSearchModal'
import {EntityService} from "@/api/AuthService";

export default {
  props: {
    readEntities: {
      type: Array,
      required: false,
      default: () => []
    },
    serviceUrl: {
      type: String,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    component: {
      type: Object,
      required: true
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  mixins: [
    TableQueryMixin
  ],
  components: {
    ExternalPatientSearchModal,
    RangeDatePicker,
    Multiselect
  },
  data () {
    return {
      ranuEntities: [],
      showExternalModal: false,
      queryUrl: this.serviceUrl + '/v2/records',
      filters: {
        querySearch: '',
        date: {
          start: null,
          end: null
        },
        statuses: [],
        exams: [],
        entities: [],
        riskFactors: []
      },
      tableOptions: {
        saveState: true,
        filterable: ['name', 'entity'],
        orderBy: {
          column: 'birthdate',
          ascending: 1
        },
        sortable: ['name', 'process_id', 'birthdate', 'external'],
        customFilters: ['start', 'end', 'statuses', 'exams', 'entities', 'risk_factors'],
        headings: {
          name: this.$t('ranu.records.name'),
          risk_factor: this.$t('ranu.records.risk_factor'),
          cc: this.$t('ranu.records.cc'),
          birthdate: this.$t('ranu.records.birthdate'),
          mother: this.$t('ranu.records.mother'),
          father: this.$t('ranu.records.father'),
          created_at: this.$t('ranu.records.created_at'),
          results: this.$t('ranu.records.results'),
          process_id: this.$t('ranu.records.process_id'),
          performed_at: this.$t('ranu.records.performed_at'),
          'entity.name': this.$t('ranu.records.entity'),
          status: this.$t('ranu.records.status'),
          external: this.$t('ranu.records.external'),
          sns_number: this.$t('ranu.records.sns_number')
        },
        columnsClasses: {
          results: 'results',
          actions: 'actions',
        },
      },
    }
  },
  mounted: function () {
    this.checkActiveFilters()
    EntityService.getRanuEntities().then(res => {
      this.ranuEntities = res
    })
  },
  methods: {
    getTableReference: function () {
      return this.$refs.ranuRecordsTable
    },
    refresh: function () {
      this.$refs.ranuRecordsTable.refresh()
    },
    handleConsult: function (record) {
      this.showExternalModal = false
      this.refresh()
      this.handleRecordClicked({ row: record }, true)
    },
    handleShowExternalModal: function () {
      this.showExternalModal = true
    },
    onLoading: function () {
      this.$emit('loading')
    },
    onLoaded: function (state) {
      this.$emit('loaded', state)
    },
    limitText (count) {
      return this.$t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')
    },
    getExamResultByType: function (selectedType, exams) {
      let activeExam = exams.find(exam => exam.exam_type.name === selectedType)

      if (!activeExam) {
        return null
      }
      if (activeExam.result_left.id === activeExam.result_right.id) {
        return this.$t(`ranu.statuses.${activeExam.result_left.id}`, activeExam.result_left.name)
      }
      if (activeExam.result_left.id === 2 || activeExam.result_right.id === 2) {
        return this.$t('ranu.statistics.refer')
      }
      return this.$t(`ranu.statuses.${activeExam.result_left.id}`, activeExam.result_left.name)
    },
    getParentByType: function (selectedParentType, contacts) {
      let parent = contacts.find(_parent => _parent.type === selectedParentType)
      return parent ? parent.name : null
    },
    checkActiveFilters: function () {
      return new Promise((resolve) => {
        this.$emit('filters-updated', this.activeFilters)
        if (this.activeFilters > 0) {
          this.$emit('update:showFilters', true)
        }
        resolve()
      })
    },
    handleRecordClicked: function ({ row }, externalRecordPermission = false) {
      this.$router.push({ name: `${this.serviceSlug}ranu/id`, params: { id: row.id, externalRecordPermission: externalRecordPermission.toString() } })
    }
  },
  computed: {
    ...mapState({
      'statusesOptions': (state) => state.ranu.statusesOptions,
      'examTypesOptions': (state) => state.ranu.examTypesOptions,
      'riskFactorsOptions': (state) => state.ranu.riskFactorsOptions
    }),
    columns: function () {
      let items = [
        'process_id',
        'sns_number',
        'name',
        'risk_factor',
        'birthdate',
        'mother',
        'external',
        'status',
        'O.E.A.1',
        'O.E.A.2',
        'P.E.A.A.',
        'Consulta',
        'P.E.A.T.C.'
      ]
      if (this.readEntities.length > 1) {
        items.splice(2, 0, 'entity.name')
        return items
      }
      return items
    },
    ranuService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ranu_server)
    },
    activeFilters: function () {
      let count = 0
      if (this.filters.entities.length > 0) count++
      if (this.filters.date.start || this.filters.date.end) count++
      if (this.filters.statuses.length > 0) count++
      if (this.filters.exams.length > 0) count++
      if (this.filters.riskFactors.length > 0) count++
      return count
    },
  },
  watch: {
    querySearch: {
      handler: function (value) {
        this.filters.querySearch = value || ''
      },
      immediate: true
    },
    customQueries: {
      handler: function (value) {
        if (value) {
          if (value.entities && value.entities !== '') {
            let entities = value.entities.split(',').map(entityId => parseInt(entityId))
            this.filters.entities = this.readEntities.filter(entity => entities.includes(entity.id))
          }
          if (value.statuses && value.statuses !== '') {
            let statuses = value.statuses.split(',').map(status => parseInt(status))
            this.filters.statuses = this.statusesOptions.filter(status => statuses.includes(status.id))
          }
          if (value.exams && value.exams !== '') {
            let exams = value.exams.split(',').map(examId => parseInt(examId))
            this.filters.exams = this.examTypesOptions.filter(exam => exams.includes(exam.id))
          }
          if (value.risk_factors && value.risk_factors !== '') {
            let riskFactors = value.risk_factors.split(',').map(riskFactorId => parseInt(riskFactorId))
            this.filters.riskFactors = this.riskFactorsOptions.filter(riskFactor => riskFactors.includes(riskFactor.id))
          }
          if (value.start && value.start !== '') {
            this.filters.date.start = value.start
          }
          if (value.end && value.end !== '') {
            this.filters.date.end = value.end
          }
        }
      },
      immediate: true
    },
    'filters.querySearch': function (value) {
      this.$refs.ranuRecordsTable.setFilter(value)
    },
    'filters.date': function (value) {
      this.$refs.ranuRecordsTable.customQueries.start = value.start || ''
      this.emitTableFilter('vue-tables.ranuRecordsTable.filter::end', value.end || '')
    },
    'filters.statuses': function (filter) {
      this.emitTableFilter('vue-tables.ranuRecordsTable.filter::statuses', filter.map(status => status.id).join(','))
    },
    'filters.exams': function (filter) {
      this.emitTableFilter('vue-tables.ranuRecordsTable.filter::exams', filter.map(exam => exam.id).join(','))
    },
    'filters.riskFactors': function (filter) {
      this.emitTableFilter('vue-tables.ranuRecordsTable.filter::risk_factors', filter.map(riskFactor => riskFactor.id).join(','))
    },
    'filters.entities': function (filter) {
      this.emitTableFilter('vue-tables.ranuRecordsTable.filter::entities', filter.map(entity => entity.id).join(','))
    },
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    }
  }
}
</script>

<style lang="scss">
.external-btn {
  line-height: 1 !important;
  margin-top: 0 !important;
}

#ranuRecordsTable .VueTables__search {
  float: right !important;
}

</style>
